import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../i18n';
import { Card } from '../../../components/Shared/Card';
import { Badge } from '../../../components/Shared/Badge';
import { EmptyState } from '../../../components/Shared/EmptyState';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/Shared/Table';
import { useSupportStore } from '../../../store/useSupportStore';
import { useAdminTenantStore } from '../../../store/useAdminTenantStore';
import { SupportTicketStatus, SupportTicketType } from '../../../api/types/admin';
import { formatDate } from '../../../utils/dateUtils';

const TYPE_KEY: Record<number, string> = {
  [SupportTicketType.Question]: 'question',
  [SupportTicketType.Problem]: 'problem',
  [SupportTicketType.NewTenantRequest]: 'newTenantRequest',
  [SupportTicketType.MeetingRequest]: 'meetingRequest',
  [SupportTicketType.Other]: 'other',
};

function typeColor(typ: number | undefined): 'gray' | 'blue' | 'red' | 'green' | 'purple' | 'yellow' {
  switch (typ) {
    case SupportTicketType.Problem:
      return 'red';
    case SupportTicketType.NewTenantRequest:
      return 'green';
    case SupportTicketType.MeetingRequest:
      return 'purple';
    case SupportTicketType.Question:
      return 'blue';
    default:
      return 'gray';
  }
}

export default function SupportInboxPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);
  const { tickets, filters, loading, error, setFilters, fetchAll } = useSupportStore();
  const { tenants, fetchAll: fetchTenants } = useAdminTenantStore();

  useEffect(() => {
    if (filters.status === undefined) {
      setFilters({ status: SupportTicketStatus.New });
    }
    fetchAll();
    if (tenants.length === 0) fetchTenants();
    // optional polling — gentle 60s
    const id = window.setInterval(() => fetchAll(), 60_000);
    return () => window.clearInterval(id);
  }, [fetchAll, fetchTenants, setFilters, tenants.length, filters.status]);

  const tenantNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const tn of tenants) {
      if (tn.id) map[tn.id] = tn.name || tn.id;
    }
    return map;
  }, [tenants]);

  const setStatusTab = (status: SupportTicketStatus) => {
    setFilters({ status });
    fetchAll();
  };

  const tabClass = (active: boolean) =>
    `px-3 py-1.5 text-[13px] font-medium rounded-md transition-colors ${
      active ? 'bg-[#00A871] text-white' : 'text-slate-600 hover:bg-slate-100'
    }`;

  const newCount = tickets.filter((t0) => t0.status === SupportTicketStatus.New).length;

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('admin:support.title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">{t('admin:support.subtitle')}</p>
          </div>
        </div>
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1">
            <button
              type="button"
              className={tabClass(filters.status === SupportTicketStatus.New)}
              onClick={() => setStatusTab(SupportTicketStatus.New)}
            >
              {t('admin:support.tabs.new')}
              {newCount > 0 && filters.status !== SupportTicketStatus.New && (
                <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white text-[10px]">
                  {newCount}
                </span>
              )}
            </button>
            <button
              type="button"
              className={tabClass(filters.status === SupportTicketStatus.InProgress)}
              onClick={() => setStatusTab(SupportTicketStatus.InProgress)}
            >
              {t('admin:support.tabs.inProgress')}
            </button>
            <button
              type="button"
              className={tabClass(filters.status === SupportTicketStatus.Closed)}
              onClick={() => setStatusTab(SupportTicketStatus.Closed)}
            >
              {t('admin:support.tabs.closed')}
            </button>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <select
              value={filters.type ?? 'all'}
              onChange={(e) => {
                setFilters({
                  type: e.target.value === 'all'
                    ? undefined
                    : (Number(e.target.value) as 0 | 1 | 2 | 3 | 99),
                });
                fetchAll();
              }}
              className="px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
            >
              <option value="all">{t('admin:support.filters.all')}</option>
              <option value={SupportTicketType.Question}>{t('admin:support.types.question')}</option>
              <option value={SupportTicketType.Problem}>{t('admin:support.types.problem')}</option>
              <option value={SupportTicketType.NewTenantRequest}>{t('admin:support.types.newTenantRequest')}</option>
              <option value={SupportTicketType.MeetingRequest}>{t('admin:support.types.meetingRequest')}</option>
              <option value={SupportTicketType.Other}>{t('admin:support.types.other')}</option>
            </select>

            <select
              value={filters.tenantId ?? 'all'}
              onChange={(e) => {
                setFilters({ tenantId: e.target.value === 'all' ? undefined : e.target.value });
                fetchAll();
              }}
              className="px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
            >
              <option value="all">{t('admin:support.filters.all')}</option>
              {tenants.map((tn) => (
                <option key={tn.id} value={tn.id}>
                  {tn.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {error ? (
        <Card>
          <EmptyState
            variant="error"
            title={t('admin:support.errors.fetchFailed')}
            onRetry={fetchAll}
          />
        </Card>
      ) : loading && tickets.length === 0 ? (
        <Card>
          <p className="text-[13px] text-slate-500 text-center py-6">{t('common:actions.loading')}</p>
        </Card>
      ) : tickets.length === 0 ? (
        <Card>
          <EmptyState title={t('admin:support.empty')} />
        </Card>
      ) : (
        <Card noPadding>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader className="w-6"><span className="sr-only">·</span></TableHeader>
                <TableHeader>{t('admin:support.fields.date')}</TableHeader>
                <TableHeader>{t('admin:support.fields.type')}</TableHeader>
                <TableHeader className="hidden sm:table-cell">{t('admin:support.fields.tenant')}</TableHeader>
                <TableHeader>{t('admin:support.fields.subject')}</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((tkt) => (
                <TableRow key={tkt.id} onClick={() => navigate(`/admin/support/${tkt.id}`)}>
                  <TableCell>
                    {tkt.status === SupportTicketStatus.New && (
                      <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{formatDate(tkt.createdAt)}</TableCell>
                  <TableCell>
                    <Badge color={typeColor(tkt.type)}>
                      {t(`admin:support.types.${TYPE_KEY[tkt.type ?? -1] ?? 'other'}`)}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-slate-600">
                    {tkt.tenantId
                      ? tenantNames[tkt.tenantId] ?? tkt.tenantId
                      : t('admin:support.fields.anonymous')}
                  </TableCell>
                  <TableCell className="font-medium text-slate-900">
                    {tkt.subject}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
